import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ChevronLeft, RefreshCw, CheckCircle2, Mail, Trash2, RotateCcw } from "lucide-react";
import { Ornament } from "../components/Ornament";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AdminUsers() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [onlyUnverified, setOnlyUnverified] = useState(false);
  const [actionFor, setActionFor] = useState(null);
  const [toast, setToast] = useState("");

  const load = async () => {
    setBusy(true); setErr("");
    try {
      const res = await api.get("/admin/users");
      setData(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Couldn't load users.");
    } finally { setBusy(false); }
  };

  useEffect(() => { if (user?.is_admin) load(); /* eslint-disable-next-line */ }, [user?.is_admin]);

  const filtered = useMemo(() => {
    if (!data?.users) return [];
    const q = query.trim().toLowerCase();
    return data.users.filter((u) => {
      if (onlyUnverified && u.email_verified) return false;
      if (!q) return true;
      return (u.email || "").toLowerCase().includes(q)
          || (u.name || "").toLowerCase().includes(q)
          || (u.storyteller_name || "").toLowerCase().includes(q);
    });
  }, [data, query, onlyUnverified]);

  const doAction = async (uid, kind) => {
    setActionFor(`${uid}:${kind}`);
    setToast("");
    try {
      if (kind === "verify") {
        const res = await api.post("/admin/users/verify", { user_id: uid });
        setToast(`Verified ${res.data.user_id}.`);
      } else if (kind === "resend") {
        const res = await api.post("/admin/users/resend-verification", { user_id: uid });
        setToast(res.data.already_verified ? "That user is already verified." : `Resend queued (id ${(res.data.email_id || "pending").slice(0, 12)}…).`);
      } else if (kind === "reset-sw") {
        const ok = window.confirm("Reset Story Week for this user? Their answers stay put but Day 1 timing restarts today. They'll receive the daily unlock emails fresh.");
        if (!ok) { setActionFor(null); return; }
        const res = await api.post("/admin/users/reset-story-week", { user_id: uid });
        setToast(`Story Week reset (${res.data.sessions_reset} session${res.data.sessions_reset === 1 ? "" : "s"}).`);
      } else if (kind === "delete") {
        const ok = window.confirm("Delete this user AND every piece of their data forever? This cannot be undone.");
        if (!ok) { setActionFor(null); return; }
        const res = await api.post("/admin/users/delete", { user_id: uid });
        const total = Object.values(res.data.deleted || {}).reduce((a, b) => a + b, 0);
        setToast(`Deleted user + ${total} record${total === 1 ? "" : "s"}.`);
      }
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setToast(typeof detail === "string" ? detail : "Action failed. Please try again.");
    } finally {
      setActionFor(null);
      setTimeout(() => setToast(""), 5000);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center paper"><p className="italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.3rem" }}>Loading…</p></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) {
    return (
      <div className="min-h-screen paper flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <p className="label-ui text-[11px] text-[#B8973A] tracking-[0.25em]">ADMINS ONLY</p>
          <h1 className="text-3xl mt-4 mb-4" style={{ fontFamily: "Cormorant Garamond, serif" }}>This page is for the team.</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen paper" data-testid="admin-users-page">
      <header className="px-6 sm:px-10 py-6 flex items-center justify-between hairline-cream border-b">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-[#8C8278] hover:text-[#111D33]" data-testid="admin-users-back">
          <ChevronLeft size={16} /><span className="label-ui text-[12px]">Back to dashboard</span>
        </Link>
        <span className="label-ui text-[11px] text-[#B8973A] tracking-[0.25em]">ADMIN &middot; USERS</span>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-10 py-10">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>Users</h1>
          <p className="mt-2 text-[#8C8278] italic" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem" }}>
            Everyone signed up. Verify manually if a confirmation email doesn't land.
          </p>
        </div>

        {err && <div className="mb-6 p-4" style={{ background: "#FDECEC", border: "1px solid #E8B4B4", borderRadius: 4 }}><p className="text-[#8A1F1F]" style={{ fontFamily: "Georgia, serif" }}>{err}</p></div>}
        {toast && <div className="mb-6 p-4" style={{ background: "#F0F7EE", border: "1px solid #C7DEBE", borderRadius: 4 }} data-testid="admin-users-toast"><p className="text-[#3D5C2E]" style={{ fontFamily: "Georgia, serif" }}>{toast}</p></div>}

        {data && (
          <>
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search email, name, or storyteller…"
                     className="flex-1 px-4 py-3 bg-white hairline-cream" style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "15px" }}
                     data-testid="admin-users-search" />
              <label className="inline-flex items-center gap-2 px-4 py-3 bg-white hairline-cream cursor-pointer" style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "14px" }}>
                <input type="checkbox" checked={onlyUnverified} onChange={(e) => setOnlyUnverified(e.target.checked)} data-testid="admin-users-unverified-toggle" />
                Unverified only
              </label>
              <button type="button" onClick={load} disabled={busy}
                      className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-white hairline-cream text-[#1B2A4A] hover:bg-[#F0EAE0]"
                      style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "14px", minHeight: 48 }}
                      data-testid="admin-users-refresh">
                <RefreshCw size={16} />{busy ? "Loading…" : "Refresh"}
              </button>
            </div>

            <div className="bg-white hairline-cream overflow-hidden" style={{ borderRadius: 4 }}>
              {filtered.length === 0 ? (
                <div className="p-10 text-center"><p className="italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem" }}>No users match.</p></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left" data-testid="admin-users-table">
                    <thead>
                      <tr style={{ background: "#F7F0E0" }}>
                        <th className="px-4 py-3 label-ui text-[10px] text-[#8C8278] tracking-[0.2em]">JOINED</th>
                        <th className="px-4 py-3 label-ui text-[10px] text-[#8C8278] tracking-[0.2em]">NAME</th>
                        <th className="px-4 py-3 label-ui text-[10px] text-[#8C8278] tracking-[0.2em]">EMAIL</th>
                        <th className="px-4 py-3 label-ui text-[10px] text-[#8C8278] tracking-[0.2em]">STORYTELLER</th>
                        <th className="px-4 py-3 label-ui text-[10px] text-[#8C8278] tracking-[0.2em]">STATUS</th>
                        <th className="px-4 py-3 label-ui text-[10px] text-[#8C8278] tracking-[0.2em]">ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((u) => (
                        <tr key={u.user_id} className="hairline-cream border-t align-top" data-testid={`admin-user-row-${u.user_id}`}>
                          <td className="px-4 py-3 text-sm text-[#8C8278]" style={{ fontFamily: "Georgia, serif" }}>{formatDate(u.created_at)}</td>
                          <td className="px-4 py-3 text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", fontSize: "15px" }}>{u.name || <span className="text-[#B0A79A] italic">—</span>}</td>
                          <td className="px-4 py-3 text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", fontSize: "15px" }}>
                            <a href={`mailto:${u.email}`} className="hover:underline text-[#B8973A]">{u.email}</a>
                          </td>
                          <td className="px-4 py-3 text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", fontSize: "14px" }}>
                            {u.storyteller_name ? `${u.storyteller_name}${u.storyteller_relationship ? ` (${u.storyteller_relationship})` : ""}` : <span className="text-[#B0A79A] italic">—</span>}
                          </td>
                          <td className="px-4 py-3">
                            {u.email_verified ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 label-ui text-[10px] tracking-[0.15em]" style={{ background: "#F0F7EE", color: "#3D5C2E", borderRadius: 3 }}>
                                <CheckCircle2 size={12} /> VERIFIED
                              </span>
                            ) : (
                              <span className="inline-block px-2 py-1 label-ui text-[10px] tracking-[0.15em]" style={{ background: "#FDF3E1", color: "#8C6A20", borderRadius: 3 }}>
                                UNVERIFIED
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col sm:flex-row gap-2">
                              {!u.email_verified && (
                                <>
                                  <button type="button" onClick={() => doAction(u.user_id, "resend")} disabled={actionFor === `${u.user_id}:resend`}
                                          className="inline-flex items-center gap-1 px-3 py-2 bg-white hairline-cream text-[#1B2A4A] hover:bg-[#F0EAE0] transition disabled:opacity-40"
                                          style={{ borderRadius: 3, fontFamily: "Georgia, serif", fontSize: "13px", minHeight: 36 }}
                                          data-testid={`admin-user-resend-${u.user_id}`}>
                                    <Mail size={13} />{actionFor === `${u.user_id}:resend` ? "Sending…" : "Resend"}
                                  </button>
                                  <button type="button" onClick={() => doAction(u.user_id, "verify")} disabled={actionFor === `${u.user_id}:verify`}
                                          className="inline-flex items-center gap-1 px-3 py-2 bg-[#1B2A4A] text-[#E8D5A3] hover:bg-[#111D33] transition disabled:opacity-40"
                                          style={{ borderRadius: 3, fontFamily: "Georgia, serif", fontSize: "13px", minHeight: 36 }}
                                          data-testid={`admin-user-verify-${u.user_id}`}>
                                    <CheckCircle2 size={13} />{actionFor === `${u.user_id}:verify` ? "Verifying…" : "Verify manually"}
                                  </button>
                                </>
                              )}
                              <button type="button" onClick={() => doAction(u.user_id, "reset-sw")} disabled={actionFor === `${u.user_id}:reset-sw`}
                                      className="inline-flex items-center gap-1 px-3 py-2 bg-white hairline-cream text-[#8C6A20] hover:bg-[#FDF3E1] transition disabled:opacity-40"
                                      style={{ borderRadius: 3, fontFamily: "Georgia, serif", fontSize: "13px", minHeight: 36 }}
                                      title="Restart Story Week from Day 1 for this user (keeps their answers)."
                                      data-testid={`admin-user-reset-sw-${u.user_id}`}>
                                <RotateCcw size={13} />{actionFor === `${u.user_id}:reset-sw` ? "Resetting…" : "Reset Story Week"}
                              </button>
                              <button type="button" onClick={() => doAction(u.user_id, "delete")} disabled={actionFor === `${u.user_id}:delete`}
                                      className="inline-flex items-center gap-1 px-3 py-2 bg-white hairline-cream text-[#8A1F1F] hover:bg-[#FDECEC] transition disabled:opacity-40"
                                      style={{ borderRadius: 3, fontFamily: "Georgia, serif", fontSize: "13px", minHeight: 36 }}
                                      title="Delete this user and every trace of them. Cannot be undone."
                                      data-testid={`admin-user-delete-${u.user_id}`}>
                                <Trash2 size={13} />{actionFor === `${u.user_id}:delete` ? "Deleting…" : "Delete"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </main>
      <footer className="py-8 text-center"><Ornament color="#B8973A" /></footer>
    </div>
  );
}
